import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader2, Save, Send, Sparkles, MessageSquare, CheckCircle2, RefreshCw,
  Plus, X, Palette, Globe, Mail, ArrowLeft, ArrowRight, AlertTriangle, MoveRight,
  Wand2, Zap, Search, Check, Settings2, ChevronDown, ShieldCheck,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { useOnboardingAuth } from "../contexts/OnboardingAuthContext";
import HideGlobalWidget from "../components/HideGlobalWidget";
import { AiDisclosureNotice } from "../components/chatbot/AiDisclosureNotice";

const TONE_LABELS = {
  "kort-och-rak": "Kort & rak",
  "varm-och-personlig": "Varm & personlig",
  "professionell-och-serviceinriktad": "Professionell",
  "lokal-och-vardaglig": "Lokal & vardaglig",
  "premium-och-trygg": "Premium & trygg",
};

const STATUS_BADGE = {
  analyzing: { label: "Analyserar", cls: "bg-blue-50 text-blue-800 border-blue-200" },
  draft: { label: "Utkast", cls: "bg-slate-50 text-slate-700 border-slate-200" },
  trial_requested: { label: "Trial begärd", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  approved: { label: "Godkänd", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  promoted: { label: "Aktiverad", cls: "bg-[#dcefe3] text-[#1c5b32] border-[#a7d4b6]" },
  rejected: { label: "Avvisad", cls: "bg-red-50 text-red-800 border-red-200" },
};

// Steps shown in the "AI is working" animation while status === "analyzing"
const AI_STEPS = [
  { icon: Globe, label: "Hämtar er hemsida" },
  { icon: Search, label: "Läser innehåll, struktur och bilder" },
  { icon: Sparkles, label: "Identifierar bransch och ton" },
  { icon: MessageSquare, label: "Skapar testfrågor och FAQ" },
  { icon: Wand2, label: "Färdigställer utkastet" },
];

export default function OnboardingDraftPage() {
  const { draftId } = useParams();
  const navigate = useNavigate();
  const { isAuthed, loading: authLoading, authedFetch } = useOnboardingAuth();

  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submittingTrial, setSubmittingTrial] = useState(false);
  const [form, setForm] = useState(null);
  const formEstablishedRef = useRef(false);
  const pollRef = useRef(null);

  // v3.50: anonymous-first onboarding. If the draft was created without
  // auth on /skapa-ai-agent, an anon_token lives in localStorage. The
  // draft page uses /draft-anon/* read-only until the user logs in and
  // we claim the draft.
  const ANON_DRAFT_KEY = "elido_onboarding_anon_drafts";
  const _readAnonToken = useCallback(() => {
    try {
      const raw = localStorage.getItem(ANON_DRAFT_KEY);
      const map = raw ? JSON.parse(raw) : {};
      return map[draftId] || null;
    } catch (_) { return null; }
  }, [draftId]);
  const _clearAnonToken = useCallback(() => {
    try {
      const raw = localStorage.getItem(ANON_DRAFT_KEY);
      const map = raw ? JSON.parse(raw) : {};
      delete map[draftId];
      localStorage.setItem(ANON_DRAFT_KEY, JSON.stringify(map));
    } catch (_) { /* ignore */ }
  }, [draftId]);

  const [anonToken, setAnonToken] = useState(() => {
    try {
      const raw = localStorage.getItem("elido_onboarding_anon_drafts");
      const map = raw ? JSON.parse(raw) : {};
      return map[draftId] || null;
    } catch (_) { return null; }
  });
  // True while user is anonymous AND we still own the draft via anon_token.
  // Toggles read-only mode + shows the "Logga in för att spara"-banner.
  const isAnonMode = !isAuthed && !!anonToken;

  const loadDraft = useCallback(async () => {
    try {
      let res;
      if (isAuthed) {
        res = await authedFetch(`/api/onboarding/draft/${draftId}`);
      } else if (anonToken) {
        res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/onboarding/draft-anon/${draftId}`, {
          headers: { "X-Anon-Token": anonToken },
        });
      } else {
        // No way to view this draft → bounce to create
        navigate("/skapa-ai-agent");
        return null;
      }
      if (res.status === 404) {
        toast.error("Utkast hittades inte");
        navigate("/skapa-ai-agent");
        return null;
      }
      if (res.status === 403) {
        toast.error("Du har inte behörighet till detta utkast");
        navigate("/skapa-ai-agent");
        return null;
      }
      const data = await res.json();
      setDraft(data.draft);
      if (!formEstablishedRef.current && data.draft.status && data.draft.status !== "analyzing") {
        setForm(_draftToForm(data.draft));
        formEstablishedRef.current = true;
      }
      return data.draft;
    } catch (e) {
      toast.error("Kunde inte läsa utkast");
      return null;
    }
  }, [authedFetch, draftId, navigate, isAuthed, anonToken]);

  useEffect(() => {
    if (authLoading) return;
    // Path A: logged in — if we still hold an anon_token for this draft,
    // claim it on the server and remove the token. After claim, regular
    // /draft/:id fetch works as owner.
    if (isAuthed && anonToken) {
      (async () => {
        try {
          const res = await authedFetch(
            `/api/onboarding/draft-anon/${draftId}/claim`,
            {
              method: "POST",
              headers: { "X-Anon-Token": anonToken },
              body: JSON.stringify({}),
            }
          );
          if (res.ok) {
            _clearAnonToken();
            setAnonToken(null);
            toast.success("Utkastet är sparat på ditt konto");
          }
        } catch (_) { /* non-fatal — user can still view via owner path */ }
      })();
      return;
    }
    // Path B: logged in without anon_token → owner fetch.
    if (isAuthed) {
      setLoading(true);
      formEstablishedRef.current = false;
      loadDraft().finally(() => setLoading(false));
      return;
    }
    // Path C: anonymous with valid token → /draft-anon fetch.
    if (anonToken) {
      setLoading(true);
      formEstablishedRef.current = false;
      loadDraft().finally(() => setLoading(false));
      return;
    }
    // Path D: anonymous WITHOUT token → can't view; bounce.
    navigate("/skapa-ai-agent", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, authLoading, draftId, anonToken]);

  // Poll while analyzing — fast enough to feel live
  useEffect(() => {
    if (draft?.status !== "analyzing") {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(loadDraft, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [draft?.status, loadDraft]);

  // v3.50: unified fetch wrapper that handles BOTH authed owner and
  // anonymous (anon_token) paths. Used for draft-scoped endpoints
  // (chat, tone-tasting, logo) that now accept either credential.
  const draftFetch = useCallback(async (urlPath, options = {}) => {
    if (isAuthed) {
      return authedFetch(urlPath, options);
    }
    // Anonymous — call the same endpoint but add X-Anon-Token instead
    // of the bearer token.
    const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
    if (anonToken) headers["X-Anon-Token"] = anonToken;
    return fetch(`${process.env.REACT_APP_BACKEND_URL || ""}${urlPath}`, {
      ...options,
      headers,
    });
  }, [isAuthed, authedFetch, anonToken]);

  const _promptLoginForAnon = useCallback((reason) => {
    toast.error(reason || "Logga in för att fortsätta");
    navigate(
      "/skapa-ai-agent/logga-in?return_to=" +
      encodeURIComponent(window.location.pathname)
    );
  }, [navigate]);

  const handleSave = async () => {
    if (!form) return null;
    if (isAnonMode) {
      _promptLoginForAnon("Logga in för att spara utkastet");
      return null;
    }
    setSaving(true);
    try {
      const payload = _formToPatch(form);
      const res = await authedFetch(`/api/onboarding/draft/${draftId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || "Kunde inte spara");
        return null;
      }
      setDraft(data.draft);
      toast.success("Sparat");
      return data.draft;
    } catch (e) {
      toast.error("Nätverksfel — kunde inte spara");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleRequestTrial = async () => {
    if (isAnonMode) {
      _promptLoginForAnon("Logga in för att begära trial");
      return;
    }
    const completeness = _computeFormCompleteness(form);
    if (!completeness.isComplete) {
      toast.error(
        "Utkastet är ofullständigt — fyll i: " +
        (completeness.missingLabels.join(", ") || "obligatoriska fält")
      );
      return;
    }
    if (!window.confirm(
      "Vi skickar utkastet till Elido för granskning. " +
      "Du får installationskod via mejl när trial:en är godkänd. Fortsätta?"
    )) return;
    setSubmittingTrial(true);
    try {
      const saved = await handleSave();
      if (!saved) return;
      const res = await authedFetch(`/api/onboarding/draft/${draftId}/request-trial`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Backend returns either a string detail OR a structured object with
        // `missing_labels` when the draft is incomplete. Surface a helpful
        // message in both cases.
        const detail = data.detail;
        if (detail && typeof detail === "object" && Array.isArray(detail.missing_labels)) {
          toast.error(
            (detail.message || "Utkastet är ofullständigt") +
            (detail.missing_labels.length ? ": " + detail.missing_labels.join(", ") : "")
          );
        } else {
          toast.error((typeof detail === "string" ? detail : null) || "Kunde inte begära trial");
        }
        return;
      }
      toast.success("Trial-förfrågan skickad!");
      await loadDraft();
    } finally {
      setSubmittingTrial(false);
    }
  };

  if (authLoading || loading || !draft) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F2A30] via-[#15414A] to-[#0F2A30]" data-testid="onboarding-draft-loading">
        <Loader2 className="w-6 h-6 text-white/60 animate-spin" />
      </main>
    );
  }

  // While analyzing show the "AI is working" experience — premium and reassuring.
  if (draft.status === "analyzing") {
    return <AnalyzingExperience draft={draft} />;
  }

  // After the user has requested a trial, replace the editor with a dedicated
  // celebratory landing — this is the "wow, det är gjort"-moment and should
  // feel premium and motivating, not just a small banner.
  if (draft.status === "trial_requested" && form) {
    return <TrialSuccessExperience form={form} draft={draft} onBack={() => navigate("/skapa-ai-agent")} />;
  }

  if (!form) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F2A30] via-[#15414A] to-[#0F2A30]">
        <Loader2 className="w-6 h-6 text-white/60 animate-spin" />
      </main>
    );
  }

  const status = draft.status || "draft";
  // Server-computed completeness: backend tells us if essential fields are
  // filled. We mirror it locally against the form (which holds unsaved edits)
  // so the trial button reacts immediately as the user types instead of
  // waiting for a save round-trip. Plain function call — not useMemo — to
  // avoid hook-order violations against the early returns above.
  const localCompleteness = _computeFormCompleteness(form);
  const isComplete = localCompleteness.isComplete;
  const missingLabels = localCompleteness.missingLabels;
  // "Behöver fyllas i" badge wins over the generic "Utkast" label when
  // essential info is missing — keeps the user from thinking it's "approved".
  const badge =
    !isComplete && status === "draft"
      ? { label: "Ofullständigt utkast", cls: "bg-amber-50 text-amber-800 border-amber-200" }
      : (STATUS_BADGE[status] || STATUS_BADGE.draft);
  const isTrialRequested = status === "trial_requested" || status === "approved" || status === "promoted";
  const isLocked = status === "promoted" || status === "rejected";

  return (
    <main
      className="min-h-screen bg-gradient-to-br from-[#0F2A30] via-[#15414A] to-[#0F2A30] text-white"
      data-testid="onboarding-draft-page"
    >
      <HideGlobalWidget />
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 py-8">
        {/* Sticky top bar */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-7">
          <div className="flex items-center gap-3">
            <a
              href="/"
              className="flex items-center hover:opacity-80 transition-opacity"
              aria-label="Gå till startsidan"
              data-testid="onboarding-draft-logo"
            >
              <img src="/elido-logo.png" alt="Elido" className="h-7 w-auto" />
            </a>
            <span className="text-white/20">·</span>
            <button
              onClick={() => navigate("/skapa-ai-agent")}
              className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1.5 transition-colors px-2.5 py-1 rounded-full hover:bg-white/[0.04]"
              data-testid="onboarding-draft-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Tillbaks
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={loadDraft}
              className="text-white/70 hover:text-white hover:bg-white/[0.06]"
              data-testid="onboarding-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            {!isLocked && !isAnonMode && (
              <Button
                onClick={handleSave}
                disabled={saving}
                size="sm"
                variant="outline"
                className="border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08] hover:text-white"
                data-testid="onboarding-save-btn"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                Spara
              </Button>
            )}
            {isAnonMode && (
              <Button
                onClick={() => navigate("/skapa-ai-agent/logga-in?return_to=" + encodeURIComponent(window.location.pathname))}
                size="sm"
                className="bg-[#7FD4DE] text-[#0F2A30] hover:bg-[#A8E6D8] font-semibold shadow-lg shadow-cyan-500/20"
                data-testid="onboarding-anon-login-btn"
              >
                Logga in för att spara
              </Button>
            )}
          </div>
        </div>

        {/* v3.50: Anonymous-mode banner. We've already analysed the
            site and loaded the demo — now the user can see it, but
            to save edits or request a trial they need to log in. */}
        {isAnonMode && draft?.status && draft.status !== "analyzing" && (
          <div
            className="mb-6 rounded-xl border border-[#7FD4DE]/30 bg-gradient-to-r from-[#7FD4DE]/[0.08] to-transparent px-4 py-3.5 flex items-start gap-3"
            data-testid="onboarding-anon-banner"
          >
            <ShieldCheck className="w-5 h-5 text-[#7FD4DE] mt-0.5 shrink-0" />
            <div className="flex-1 text-sm text-white/85 leading-relaxed">
              <div className="font-semibold text-white">Här är ditt utkast — testa fritt!</div>
              <div className="text-white/65 mt-0.5">
                Prata med AI-agenten i sandboxen nedan. När du vill spara dina ändringar eller begära en 14-dagars trial behöver du logga in.
              </div>
            </div>
            <Button
              onClick={() => navigate("/skapa-ai-agent/logga-in?return_to=" + encodeURIComponent(window.location.pathname))}
              size="sm"
              className="bg-[#7FD4DE] text-[#0F2A30] hover:bg-[#A8E6D8] font-semibold shrink-0"
              data-testid="onboarding-anon-banner-cta"
            >
              Logga in
            </Button>
          </div>
        )}

        {/* Title block */}
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#7FD4DE] mb-2">
            Elido onboarding
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight">
            {form.company_name || "Vi har skapat ett första utkast"}
          </h1>
          <div className="text-sm text-white/60 mt-2 flex items-center gap-3 flex-wrap">
            <a href={draft.website_url} target="_blank" rel="noopener noreferrer" className="text-[#7FD4DE] hover:underline flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5" /> {draft.website_url}
            </a>
            <span
              className={`text-[10px] uppercase font-semibold tracking-wider rounded-full px-2 py-0.5 border ${badge.cls}`}
              data-testid="onboarding-draft-status"
            >
              {badge.label}
            </span>
          </div>
        </div>

        {/* Banners */}
        {draft.scrape_warning && (
          <div className="bg-amber-500/10 border border-amber-400/20 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="onboarding-scrape-warning">
            <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-100 leading-relaxed">{draft.scrape_warning}</div>
          </div>
        )}
        {isTrialRequested && (
          <div className="bg-emerald-500/10 border border-emerald-400/20 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="onboarding-trial-confirmation">
            <CheckCircle2 className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />
            <div className="text-xs text-emerald-100 leading-relaxed">
              <strong>Tack!</strong> Vi går igenom utkastet och återkommer med installationskod på <strong>{form.lead_email}</strong>.
            </div>
          </div>
        )}

        {/* Main grid: 2 col editor on left (multi-card grid inside), widget mockup on right */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_440px] gap-7">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 auto-rows-min">
            <Card title="Företag" wide>
              <Field label="Företagsnamn" testId="field-company-name">
                <DarkInput
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  disabled={isLocked}
                  placeholder="Företaget AB"
                  data-testid="onboarding-company-name-input"
                />
              </Field>
              <Field label="Bransch" testId="field-industry">
                <DarkInput
                  value={form.detected_industry}
                  onChange={(e) => setForm({ ...form, detected_industry: e.target.value })}
                  disabled={isLocked}
                  placeholder="t.ex. snickare, vvs, e-handel"
                  data-testid="onboarding-industry-input"
                />
              </Field>
              <Field
                label={
                  <>
                    Lead-e-post — vart kundernas förfrågningar skickas
                    <span
                      className="text-[#FF8A8A] ml-1"
                      aria-label="obligatoriskt"
                      data-testid="onboarding-lead-email-required-asterisk"
                    >
                      *
                    </span>
                  </>
                }
                testId="field-lead-email"
              >
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                  <DarkInput
                    type="email"
                    value={form.lead_email}
                    onChange={(e) => setForm({ ...form, lead_email: e.target.value })}
                    disabled={isLocked}
                    placeholder="kundservice@företag.se"
                    className="pl-9"
                    data-testid="onboarding-lead-email-input"
                  />
                </div>
              </Field>
            </Card>

            <Card title="Tonprofil & utseende">
              <Field label="Aktuell tonprofil" testId="field-tone">
                <div className="flex items-center gap-3">
                  <div
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-[#3A8A98]/30 to-[#7FD4DE]/20 border border-[#7FD4DE]/30 text-[#A8E6D8] text-sm font-semibold"
                    data-testid="onboarding-tone-current"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    {TONE_LABELS[form.suggested_tone] || "Inte vald"}
                  </div>
                  <p className="text-[11px] text-white/45 leading-relaxed flex-1">
                    Tryck på en testfråga i förhandsgranskningen för att jämföra tre svar och välja tonen som passar er bäst.
                  </p>
                </div>
              </Field>
              <Field label="Primär färg" testId="field-color">
                <div className="flex items-center gap-3">
                  <Palette className="w-4 h-4 text-white/40" />
                  <input
                    type="color"
                    value={form.suggested_primary_color || "#255E69"}
                    onChange={(e) => setForm({ ...form, suggested_primary_color: e.target.value })}
                    disabled={isLocked}
                    className="w-10 h-10 rounded border border-white/10 cursor-pointer bg-transparent"
                    data-testid="onboarding-color-picker"
                  />
                  <DarkInput
                    value={form.suggested_primary_color || ""}
                    onChange={(e) => setForm({ ...form, suggested_primary_color: e.target.value })}
                    disabled={isLocked}
                    placeholder="#255E69"
                    className="font-mono text-sm w-32"
                  />
                </div>
              </Field>
              {draft.suggested_logo_url && (
                <Field label="Logga (hämtad från er hemsida)" testId="field-logo">
                  <div className="flex items-center gap-3 min-w-0">
                    <img
                      src={draft.suggested_logo_url}
                      alt="logo"
                      className="w-12 h-12 rounded-lg border border-white/10 object-contain bg-white p-1 shrink-0"
                      data-testid="onboarding-logo-preview"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="text-xs text-white/50 min-w-0 flex-1">
                      <a
                        href={draft.suggested_logo_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block truncate text-[#7FD4DE] hover:underline"
                        title={draft.suggested_logo_url}
                      >
                        {_shortenUrl(draft.suggested_logo_url)}
                      </a>
                      <div className="text-[10px] text-white/30 mt-0.5">Automatiskt hämtad. Hör av dig om du vill byta logga.</div>
                    </div>
                  </div>
                </Field>
              )}
            </Card>

            <Card title="Tjänster">
              <ListEditor
                items={form.suggested_services}
                placeholder="t.ex. Reparation, Service, Försäljning"
                onChange={(v) => setForm({ ...form, suggested_services: v })}
                testIdPrefix="onboarding-service"
                disabled={isLocked}
              />
            </Card>

            <Card title="Startsvar" wide>
              <Field label="Det första boten säger" testId="field-start-msg">
                <DarkTextarea
                  value={form.suggested_start_message}
                  onChange={(e) => setForm({ ...form, suggested_start_message: e.target.value })}
                  disabled={isLocked}
                  rows={2}
                  maxLength={300}
                  data-testid="onboarding-start-message-input"
                />
              </Field>
              <Field label="Proaktiv bubbla — visas innan kunden klickar" testId="field-proactive">
                <DarkInput
                  value={form.suggested_proactive_message}
                  onChange={(e) => setForm({ ...form, suggested_proactive_message: e.target.value })}
                  disabled={isLocked}
                  maxLength={200}
                  data-testid="onboarding-proactive-input"
                />
              </Field>
            </Card>

            <details className="lg:col-span-2 group rounded-2xl border border-white/10 bg-white/[0.03] open:bg-white/[0.04] transition-colors">
              <summary
                className="cursor-pointer list-none px-5 py-3.5 flex items-center justify-between text-sm text-white/80 hover:text-white"
                data-testid="onboarding-advanced-toggle"
              >
                <span className="flex items-center gap-2.5">
                  <Settings2 className="w-4 h-4 text-white/50" />
                  <span className="font-semibold">Avancerat — vad boten samlar in</span>
                  <span className="text-[11px] uppercase tracking-wider text-[#7FD4DE]/70 font-medium">
                    Rekommendation
                  </span>
                </span>
                <ChevronDown className="w-4 h-4 text-white/40 group-open:rotate-180 transition-transform" />
              </summary>
              <div className="px-5 pb-5 pt-1">
                <p className="text-[12px] text-white/55 leading-relaxed mb-3">
                  Vi har redan föreslagit vad boten ska fråga kunden om för att leverera bästa möjliga lead till er. Du kan låta detta vara — eller justera om ni vill samla något specifikt.
                </p>
                <ListEditor
                  items={form.suggested_qualification_fields}
                  placeholder="t.ex. typ_av_jobb, ort, tidsplan"
                  onChange={(v) => setForm({ ...form, suggested_qualification_fields: v })}
                  testIdPrefix="onboarding-qfield"
                  disabled={isLocked}
                  mono
                />
              </div>
            </details>

            <Card title="FAQ-förslag" wide>
              <FaqEditor
                items={form.suggested_faqs}
                onChange={(v) => setForm({ ...form, suggested_faqs: v })}
                disabled={isLocked}
              />
            </Card>
          </div>

          {/* Right: live widget mockup + Klar-att-testa CTA below */}
          <aside className="xl:sticky xl:top-6 self-start space-y-5">
            <WidgetPreviewMockup
              form={form}
              websiteUrl={draft.website_url}
              draftLogoUrl={draft.suggested_logo_url}
              draftId={draftId}
              authedFetch={draftFetch}
              disabled={isLocked}
              onTonePicked={(toneKey) => {
                setForm((prev) => ({ ...prev, suggested_tone: toneKey }));
              }}
            />

            <ElidoCareCard />

            <TrialCTAcard
              form={form}
              isLocked={isLocked}
              isTrialRequested={isTrialRequested}
              submittingTrial={submittingTrial}
              onRequestTrial={handleRequestTrial}
              isComplete={isComplete}
              missingLabels={missingLabels}
            />
          </aside>
        </div>
      </div>
    </main>
  );
}

// ── TrialSuccessExperience ─────────────────────────────────────────────

function TrialSuccessExperience({ form, draft, onBack }) {
  const company = form.company_name || "din kundmottagare";
  const leadEmail = form.lead_email || "din lead-mejl";

  return (
    <main
      className="min-h-screen bg-gradient-to-br from-[#0F2A30] via-[#15414A] to-[#0F2A30] text-white px-4 py-12 flex items-center justify-center"
      data-testid="onboarding-trial-success"
    >
      <HideGlobalWidget />
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      {/* Glow orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#7FD4DE]/20 blur-3xl rounded-full pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#3A8A98]/20 blur-3xl rounded-full pointer-events-none" />

      <div className="relative max-w-2xl w-full">
        {/* Top: Success seal */}
        <div className="text-center mb-10">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-[#7FD4DE]/30 blur-2xl rounded-full" />
            <div className="relative w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#3A8A98] to-[#7FD4DE] flex items-center justify-center shadow-2xl shadow-[#7FD4DE]/40">
              <CheckCircle2 className="w-10 h-10 text-[#0F2A30]" strokeWidth={2.5} />
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#7FD4DE] mb-3">
            14 dagars trial begärd
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold leading-[1.05] tracking-tight">
            {company} är{" "}
            <span className="bg-gradient-to-r from-[#7FD4DE] via-[#A8E6D8] to-[#7FD4DE] bg-clip-text text-transparent">
              snart live
            </span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/70 leading-relaxed max-w-xl mx-auto">
            Vi granskar utkastet manuellt och skickar installationskoden till{" "}
            <strong className="text-white">{leadEmail}</strong> så snart allt ser bra ut — vanligen inom 1 arbetsdag.
          </p>
        </div>

        {/* Timeline */}
        <div className="bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8 mb-6">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-white/50 mb-5">
            Vad händer härnäst
          </div>
          <ol className="space-y-4">
            {[
              {
                done: true,
                title: "Ditt utkast har skickats in",
                body: `Vi har tagit emot konfigurationen för ${company} och påbörjat granskning.`,
              },
              {
                done: false,
                title: "Elido kvalitetssäkrar manuellt",
                body: "Vi går igenom ton, FAQ och kvalificeringsfält så att kundmottagaren är redo att möta era kunder från första sekunden.",
              },
              {
                done: false,
                title: "Du får installationskod via mejl",
                body: <>Vi skickar en <code className="bg-white/[0.08] text-[#7FD4DE] px-1.5 py-0.5 rounded text-[10px] font-mono">&lt;script&gt;</code>-snutt till {leadEmail} som du klistrar in på er hemsida. Tar 1 minut att installera.</>,
              },
              {
                done: false,
                title: "Trial-veckan börjar när koden är på plats",
                body: "Inga avgifter under testperioden. Avsluta när du vill — eller fortsätt automatiskt när det börjar leverera leads.",
              },
            ].map((step, i) => (
              <li key={i} className="flex gap-4 items-start">
                <div className={`shrink-0 mt-0.5 w-7 h-7 rounded-full flex items-center justify-center border-2 ${
                  step.done
                    ? "bg-emerald-500/20 border-emerald-400 text-emerald-300"
                    : "bg-white/[0.04] border-white/15 text-white/40 font-mono text-xs"
                }`}>
                  {step.done ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </div>
                <div className="flex-1 pt-0.5">
                  <div className={`text-sm font-semibold ${step.done ? "text-white" : "text-white/90"}`}>
                    {step.title}
                  </div>
                  <div className="text-xs text-white/55 mt-1 leading-relaxed">{step.body}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Value props bar */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          {[
            { num: "0 kr", body: "i 14 dagar" },
            { num: "1 min", body: "att installera" },
            { num: "24/7", body: "kundsvar" },
          ].map((v, i) => (
            <div key={i} className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-3 text-center">
              <div className="text-xl font-bold bg-gradient-to-r from-[#7FD4DE] to-[#A8E6D8] bg-clip-text text-transparent">
                {v.num}
              </div>
              <div className="text-[10px] text-white/50 uppercase tracking-wider mt-0.5">{v.body}</div>
            </div>
          ))}
        </div>

        {/* Pro-tip card */}
        <div className="bg-gradient-to-br from-[#3A8A98]/15 to-transparent border border-[#7FD4DE]/20 rounded-xl p-4 flex gap-3 mb-6">
          <Sparkles className="w-4 h-4 text-[#7FD4DE] shrink-0 mt-0.5" />
          <div className="text-xs text-white/75 leading-relaxed">
            <strong className="text-white">Tips:</strong> Vill du redigera utkastet medan vi granskar? Logga in igen senare så hittar du det under <em>Dina utkast</em> — du kan justera tonen, lägga till FAQ eller ändra färg ända fram tills installationskoden skickas.
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={onBack}
            className="px-5 py-3 rounded-xl border border-white/15 text-white/80 hover:bg-white/[0.04] hover:text-white text-sm font-medium transition-colors"
            data-testid="trial-success-back"
          >
            Tillbaka till start
          </button>
          {draft?.website_url && (
            <a
              href={draft.website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-3 rounded-xl bg-white/[0.06] text-white/90 hover:bg-white/[0.1] text-sm font-medium transition-colors inline-flex items-center gap-2 justify-center"
              data-testid="trial-success-visit-site"
            >
              <Globe className="w-3.5 h-3.5" />
              Besök er hemsida
            </a>
          )}
        </div>

        <p className="text-[11px] text-white/30 text-center mt-8 leading-relaxed">
          Frågor? Mejla <a href="mailto:hej@elido.nu" className="text-[#7FD4DE] hover:underline">hej@elido.nu</a> — vi svarar inom några timmar.
        </p>
      </div>
    </main>
  );
}

// ── AnalyzingExperience ─────────────────────────────────────────────────

function AnalyzingExperience({ draft }) {
  // Tick through the AI steps so the user feels the AI is working.
  // ~2.5s per step gives a satisfying ~12s flow that mirrors real analysis time.
  const [stepIdx, setStepIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setStepIdx((s) => Math.min(AI_STEPS.length - 1, s + 1));
    }, 2500);
    return () => clearInterval(t);
  }, []);

  return (
    <main
      className="min-h-screen bg-gradient-to-br from-[#0F2A30] via-[#15414A] to-[#0F2A30] text-white flex items-center justify-center px-4 py-12"
      data-testid="onboarding-analyzing-experience"
    >
      <HideGlobalWidget />
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div className="relative max-w-2xl w-full">
        {/* Glow orb */}
        <div className="relative mx-auto w-32 h-32 mb-10">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#3A8A98] to-[#7FD4DE] blur-2xl opacity-50 animate-pulse" />
          <div className="absolute inset-3 rounded-full bg-gradient-to-br from-[#3A8A98] to-[#7FD4DE] opacity-80" />
          <div className="absolute inset-6 rounded-full bg-[#0F2A30] flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-[#7FD4DE] animate-spin" style={{ animationDuration: "3s" }} />
          </div>
          {/* Orbiting dots */}
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32"
              style={{
                animation: `ob-orbit 3s linear infinite`,
                animationDelay: `${i * -1}s`,
              }}
            >
              <div className="w-2 h-2 rounded-full bg-[#7FD4DE] absolute top-0 left-1/2 -translate-x-1/2 shadow-[0_0_12px_rgba(127,212,222,0.8)]" />
            </div>
          ))}
        </div>

        <div className="text-center mb-10">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#7FD4DE] mb-3">
            Elido Nexus arbetar
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight">
            Vi läser{" "}
            <span className="bg-gradient-to-r from-[#7FD4DE] via-[#A8E6D8] to-[#7FD4DE] bg-clip-text text-transparent">
              {(draft.website_url || "").replace(/^https?:\/\//, "").replace(/\/$/, "")}
            </span>
          </h1>
          <p className="text-sm text-white/60 mt-3 leading-relaxed">
            Vår AI hämtar er hemsida och bygger ett utkast åt er — det tar ungefär en halv minut.
          </p>
        </div>

        {/* Step list */}
        <div className="space-y-2.5">
          {AI_STEPS.map((step, i) => {
            const Icon = step.icon;
            const done = i < stepIdx;
            const active = i === stepIdx;
            return (
              <div
                key={i}
                className={`flex items-center gap-4 px-5 py-3.5 rounded-xl border transition-all duration-500 ${
                  done
                    ? "bg-emerald-500/[0.06] border-emerald-500/20"
                    : active
                    ? "bg-[#7FD4DE]/[0.08] border-[#7FD4DE]/30"
                    : "bg-white/[0.02] border-white/[0.06]"
                }`}
                data-testid={`onboarding-ai-step-${i}`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-all ${
                  done
                    ? "bg-emerald-500/20 text-emerald-300"
                    : active
                    ? "bg-[#7FD4DE]/15 text-[#7FD4DE]"
                    : "bg-white/[0.04] text-white/30"
                }`}>
                  {done ? <CheckCircle2 className="w-4 h-4" /> : active ? <Icon className="w-4 h-4 animate-pulse" /> : <Icon className="w-4 h-4" />}
                </div>
                <div className={`text-sm flex-1 transition-colors ${
                  done ? "text-white/70" : active ? "text-white font-medium" : "text-white/40"
                }`}>
                  {step.label}
                </div>
                {active && (
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#7FD4DE] animate-pulse" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#7FD4DE] animate-pulse" style={{ animationDelay: "0.2s" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#7FD4DE] animate-pulse" style={{ animationDelay: "0.4s" }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-[11px] text-white/30 text-center mt-8">
          Sidan uppdateras automatiskt när vi är klara — du behöver inte göra något.
        </p>
      </div>

      <style>{`
        @keyframes ob-orbit {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
      `}</style>
    </main>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────

function _draftToForm(d) {
  return {
    _draft_id: d.draft_id,
    company_name: d.company_name || "",
    detected_industry: d.detected_industry || "",
    suggested_tone: d.suggested_tone || "professionell-och-serviceinriktad",
    suggested_primary_color: d.suggested_primary_color || "#255E69",
    suggested_start_message: d.suggested_start_message || "Hej! Vad kan vi hjälpa dig med idag?",
    suggested_proactive_message: d.suggested_proactive_message || "Behöver du hjälp? Skriv till oss!",
    suggested_services: d.suggested_services || [],
    suggested_qualification_fields: d.suggested_qualification_fields || [],
    suggested_test_questions: d.suggested_test_questions || [],
    suggested_faqs: d.suggested_faqs || [],
    lead_email: d.lead_email || "",
  };
}

function _formToPatch(f) {
  return {
    company_name: f.company_name.trim() || null,
    detected_industry: f.detected_industry.trim() || null,
    suggested_tone: f.suggested_tone || null,
    suggested_primary_color: f.suggested_primary_color || null,
    suggested_start_message: f.suggested_start_message.trim() || null,
    suggested_proactive_message: f.suggested_proactive_message.trim() || null,
    suggested_services: f.suggested_services.filter((s) => s && s.trim()),
    suggested_qualification_fields: f.suggested_qualification_fields.filter((s) => s && s.trim()),
    suggested_test_questions: f.suggested_test_questions.filter((s) => s && s.trim()),
    suggested_faqs: f.suggested_faqs.filter((q) => (q.question || "").trim() && (q.answer || "").trim()),
    lead_email: f.lead_email.trim() || null,
  };
}

// Mirrors backend `compute_draft_completeness`. Same required minimum:
// company_name, detected_industry, valid lead_email, ≥1 service.
function _computeFormCompleteness(form) {
  const missing = [];
  const labels = {
    company_name: "Företagsnamn",
    detected_industry: "Bransch",
    lead_email: "Lead-e-post",
    suggested_services: "Minst en tjänst",
  };
  if (!form) {
    return { isComplete: false, missingFields: Object.keys(labels), missingLabels: Object.values(labels) };
  }
  if (!String(form.company_name || "").trim()) missing.push("company_name");
  if (!String(form.detected_industry || "").trim()) missing.push("detected_industry");
  const email = String(form.lead_email || "").trim();
  if (!email || !email.includes("@") || !email.split("@")[1]?.includes(".")) {
    missing.push("lead_email");
  }
  const hasService = (form.suggested_services || []).some((s) => s && String(s).trim());
  if (!hasService) missing.push("suggested_services");
  return {
    isComplete: missing.length === 0,
    missingFields: missing,
    missingLabels: missing.map((k) => labels[k]).filter(Boolean),
  };
}

// ── Trial CTA card (lives under the preview widget, with checklist + benefits) ──

function ElidoCareCard() {
  return (
    <div
      className="rounded-2xl border border-[#7FD4DE]/20 bg-gradient-to-br from-[#3A8A98]/15 via-[#1A4750]/30 to-[#0F2A30]/40 backdrop-blur-sm p-5"
      data-testid="onboarding-elido-care"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#3A8A98] to-[#7FD4DE] flex items-center justify-center shrink-0 shadow-lg shadow-[#3A8A98]/30">
          <ShieldCheck className="w-4 h-4 text-[#0F2A30]" strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#7FD4DE] mb-1">
            Elido sköter resten
          </div>
          <h3 className="text-sm font-semibold text-white leading-snug mb-2">
            När boten väl är live behöver ni inte göra något
          </h3>
          <p className="text-[12px] text-white/65 leading-relaxed">
            Vi finjusterar löpande hur boten beter sig utifrån de riktiga
            samtalen den får. Den blir vassare för varje vecka — utan att
            ni behöver lyfta ett finger. Behöver ni ändra något? Skicka ett mejl, så fixar vi det.
          </p>
        </div>
      </div>
    </div>
  );
}

function TrialCTAcard({ form, isLocked, isTrialRequested, submittingTrial, onRequestTrial, isComplete, missingLabels }) {
  if (isTrialRequested) {
    return (
      <div
        className="bg-gradient-to-br from-emerald-500/15 to-[#7FD4DE]/5 border border-emerald-400/30 rounded-2xl p-5"
        data-testid="onboarding-trial-card-success"
      >
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-300" />
          <div className="text-sm font-semibold text-emerald-100">Trial-förfrågan skickad</div>
        </div>
        <p className="text-xs text-emerald-100/80 leading-relaxed">
          Vi går igenom inställningarna manuellt och mejlar dig installationskoden så snart utkastet är godkänt.
        </p>
      </div>
    );
  }

  const benefits = [
    "Du betalar 0 kr i 14 dagar",
    "Inga kortuppgifter krävs",
    "Mejl direkt när någon vill bli kontaktad",
    "Avsluta när du vill",
  ];

  return (
    <div
      className="bg-gradient-to-br from-[#3A8A98]/20 to-[#7FD4DE]/5 border border-[#7FD4DE]/25 rounded-2xl p-5"
      data-testid="onboarding-trial-card"
    >
      <div className="flex items-start gap-2.5 mb-3">
        <div className="w-9 h-9 rounded-lg bg-[#7FD4DE]/15 flex items-center justify-center shrink-0">
          <Zap className="w-4 h-4 text-[#7FD4DE]" />
        </div>
        <div>
          <div className="text-sm font-bold text-white leading-snug">Redo att fånga fler leads?</div>
          <div className="text-[11px] text-white/60 mt-0.5 leading-relaxed">
            Aktivera 14 dagars gratis test så börjar er kundmottagare svara besökare direkt på er hemsida.
          </div>
        </div>
      </div>

      <ul className="space-y-1.5 mb-4">
        {benefits.map((b, i) => (
          <li key={i} className="flex items-center gap-2 text-[12px] text-white/80">
            <CheckCircle2 className="w-3.5 h-3.5 text-[#7FD4DE] shrink-0" />
            {b}
          </li>
        ))}
      </ul>

      {!isComplete && Array.isArray(missingLabels) && missingLabels.length > 0 && (
        <div
          className="mb-3 p-3 rounded-xl bg-amber-500/10 border border-amber-400/25"
          data-testid="onboarding-trial-incomplete-warning"
        >
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-200 mb-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            Fyll i innan du startar testet
          </div>
          <ul className="space-y-1 text-[11px] text-amber-100/80 leading-relaxed">
            {missingLabels.map((lbl) => (
              <li key={lbl} className="flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-amber-300" />
                {lbl}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Button
        onClick={onRequestTrial}
        disabled={submittingTrial || isLocked || !isComplete}
        size="lg"
        className="w-full bg-gradient-to-r from-[#3A8A98] to-[#7FD4DE] hover:from-[#2c7280] hover:to-[#6cc4cf] text-[#0F2A30] font-semibold rounded-xl shadow-lg shadow-[#3A8A98]/25 disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="onboarding-request-trial-btn"
      >
        {submittingTrial ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
        Starta 14 dagars gratis test
        {!submittingTrial && <MoveRight className="w-4 h-4 ml-2" />}
      </Button>

      <div className="text-[10px] text-white/40 mt-3 leading-relaxed text-center">
        Vi granskar utkastet och skickar installationskod till <strong className="text-white/70">{form.lead_email || "din lead-mejl"}</strong>.
      </div>
    </div>
  );
}

// ── Layout primitives ──────────────────────────────────────────────────

function Card({ title, children, wide }) {
  return (
    <section className={`bg-white/[0.03] backdrop-blur-sm border border-white/[0.08] rounded-2xl p-5 ${wide ? "lg:col-span-2" : ""}`}>
      <h2 className="text-xs font-semibold text-white/90 mb-4 flex items-center gap-2 uppercase tracking-wider">
        <Sparkles className="w-3 h-3 text-[#7FD4DE]" />
        {title}
      </h2>
      <div className="space-y-3.5">{children}</div>
    </section>
  );
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="text-[10px] font-medium text-white/55 uppercase tracking-[0.12em]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function DarkInput({ className = "", ...props }) {
  return (
    <Input
      {...props}
      className={`bg-[#0a1f24] border-white/10 text-white placeholder:text-white/25 focus-visible:ring-[#7FD4DE]/40 focus-visible:border-[#7FD4DE]/50 rounded-lg ${className}`}
    />
  );
}

function DarkTextarea({ className = "", ...props }) {
  return (
    <Textarea
      {...props}
      className={`bg-[#0a1f24] border-white/10 text-white placeholder:text-white/25 focus-visible:ring-[#7FD4DE]/40 focus-visible:border-[#7FD4DE]/50 rounded-lg ${className}`}
    />
  );
}

function ListEditor({ items, onChange, placeholder, testIdPrefix, disabled, mono }) {
  const add = () => onChange([...(items || []), ""]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, v) => onChange(items.map((it, idx) => (idx === i ? v : it)));
  return (
    <div className="space-y-2">
      {(items || []).map((it, i) => (
        <div key={i} className="flex gap-2" data-testid={`${testIdPrefix}-row-${i}`}>
          <DarkInput
            value={it}
            onChange={(e) => update(i, e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className={mono ? "font-mono text-sm" : ""}
            data-testid={`${testIdPrefix}-input-${i}`}
          />
          <button
            type="button"
            onClick={() => remove(i)}
            disabled={disabled}
            className="w-9 h-9 rounded-lg text-white/40 hover:text-white hover:bg-white/[0.06] inline-flex items-center justify-center disabled:opacity-50 transition-colors shrink-0"
            data-testid={`${testIdPrefix}-remove-${i}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        disabled={disabled}
        className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/10 rounded-lg hover:border-white/25 hover:bg-white/[0.04] transition-all disabled:opacity-50"
        data-testid={`${testIdPrefix}-add`}
      >
        <Plus className="w-3.5 h-3.5" /> Lägg till
      </button>
    </div>
  );
}

function FaqEditor({ items, onChange, disabled }) {
  const add = () => onChange([...(items || []), { question: "", answer: "" }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, key, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [key]: v } : it)));
  return (
    <div className="space-y-2.5">
      {(items || []).map((it, i) => (
        <div key={i} className="bg-white/[0.02] rounded-lg p-3 space-y-2 border border-white/[0.06]" data-testid={`onboarding-faq-row-${i}`}>
          <div className="flex items-center justify-between gap-2">
            <Label className="text-[10px] font-semibold uppercase tracking-wide text-white/40">FAQ #{i + 1}</Label>
            <button
              type="button"
              onClick={() => remove(i)}
              disabled={disabled}
              className="text-white/40 hover:text-white"
              data-testid={`onboarding-faq-remove-${i}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <DarkInput
            value={it.question || ""}
            onChange={(e) => update(i, "question", e.target.value)}
            placeholder="Fråga"
            disabled={disabled}
            data-testid={`onboarding-faq-q-${i}`}
          />
          <DarkTextarea
            value={it.answer || ""}
            onChange={(e) => update(i, "answer", e.target.value)}
            placeholder="Svar"
            rows={2}
            disabled={disabled}
            data-testid={`onboarding-faq-a-${i}`}
          />
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        disabled={disabled}
        className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/10 rounded-lg hover:border-white/25 hover:bg-white/[0.04] transition-all disabled:opacity-50"
        data-testid="onboarding-faq-add"
      >
        <Plus className="w-3.5 h-3.5" /> Lägg till FAQ
      </button>
    </div>
  );
}

// ── Widget mockup with LIVE form-driven styling + collapsed/expanded states ────

function WidgetPreviewMockup({ form, websiteUrl, draftLogoUrl, draftId, authedFetch, disabled, onTonePicked }) {
  const [mode, setMode] = useState("collapsed"); // "collapsed" | "expanded"
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  // Tone tasting state: when a test question is clicked, we fetch 3 parallel
  // replies and present them as cards so the user can pick the voice they prefer.
  const [tasting, setTasting] = useState(null); // null | { question, loading, variants:[], pickedTone, error }
  const [tastedQuestions, setTastedQuestions] = useState([]); // questions already tasted — hidden from chips
  const scrollRef = useRef(null);
  const primaryColor = form.suggested_primary_color || "#255E69";
  const companyInitial = (form.company_name || "C").trim().charAt(0).toUpperCase();

  // Re-seed greeting whenever the start message changes — keeps preview in sync.
  useEffect(() => {
    setHistory([{ role: "assistant", content: form.suggested_start_message || "Hej!" }]);
  }, [form.suggested_start_message, draftId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history, sending]);

  // Derived gradient: lighten primaryColor by mixing with a soft tint
  const headerGradient = useMemo(() => {
    return `linear-gradient(135deg, ${primaryColor} 0%, ${_lightenHex(primaryColor, 18)} 100%)`;
  }, [primaryColor]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending || disabled) return;
    const nextHistory = [...history, { role: "user", content: msg }];
    setHistory(nextHistory);
    setInput("");
    setSending(true);
    try {
      const res = await authedFetch(`/api/onboarding/draft/${draftId}/chat`, {
        method: "POST",
        body: JSON.stringify({ message: msg, history: history }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setHistory([...nextHistory, { role: "assistant", content: "Ursäkta, något gick fel. Försök igen." }]);
      } else {
        setHistory([...nextHistory, { role: "assistant", content: data.reply || "(tomt svar)" }]);
      }
    } catch (e) {
      setHistory([...nextHistory, { role: "assistant", content: "Nätverksfel. Försök igen." }]);
    } finally {
      setSending(false);
    }
  };

  const resetChat = () => {
    setHistory([{ role: "assistant", content: form.suggested_start_message || "Hej!" }]);
    setTasting(null);
    setTastedQuestions([]);
  };

  // Tone-tasting: customer clicks one of the suggested test questions and
  // we fetch 3 parallel replies from the backend (one per tone profile).
  // They pick the voice they like — that tone is then saved on the draft.
  const startTasting = async (question) => {
    if (disabled || !question || sending) return;
    setTasting({ question, loading: true, variants: [], pickedTone: null, error: null });
    try {
      const res = await authedFetch(`/api/onboarding/draft/${draftId}/tone-tasting`, {
        method: "POST",
        body: JSON.stringify({ message: question }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setTasting({ question, loading: false, variants: [], pickedTone: null, error: data?.detail || "Något gick fel" });
        return;
      }
      setTasting({
        question,
        loading: false,
        variants: data.variants || [],
        pickedTone: null,
        error: null,
      });
    } catch (e) {
      setTasting({ question, loading: false, variants: [], pickedTone: null, error: "Nätverksfel" });
    }
  };

  const pickTone = async (variant) => {
    if (!tasting || tasting.pickedTone) return;
    setTasting({ ...tasting, pickedTone: variant.key });
    try {
      const res = await authedFetch(`/api/onboarding/draft/${draftId}/tone-tasting/pick`, {
        method: "POST",
        body: JSON.stringify({ tone: variant.key }),
      });
      if (!res.ok) throw new Error("Pick failed");
      // Inform parent so the editor's tone field updates live
      if (onTonePicked) onTonePicked(variant.key);
      // Append the chosen reply into the chat history so the conversation
      // continues naturally in the chosen voice.
      setHistory((h) => [
        ...h,
        { role: "user", content: tasting.question },
        { role: "assistant", content: variant.reply },
      ]);
      // Mark this question as "done" so it disappears from the chip list
      setTastedQuestions((prev) => prev.includes(tasting.question) ? prev : [...prev, tasting.question]);
      // Show a brief celebration toast
      toast.success(`Tonen "${variant.label}" är vald — boten pratar så här framöver.`);
      // Auto-dismiss the tasting card after a beat
      setTimeout(() => setTasting(null), 1200);
    } catch (e) {
      toast.error("Kunde inte spara — försök igen.");
      setTasting({ ...tasting, pickedTone: null });
    }
  };

  const cancelTasting = () => setTasting(null);

  return (
    <div className="space-y-3" data-testid="onboarding-widget-mockup">
      {/* Mode toggle */}
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] font-semibold text-white/40">
        <button
          onClick={() => setMode("collapsed")}
          className={`px-2.5 py-1 rounded-md transition-colors ${mode === "collapsed" ? "bg-white/[0.08] text-white" : "hover:text-white/70"}`}
          data-testid="widget-mode-collapsed"
        >
          Stängd (proaktiv)
        </button>
        <button
          onClick={() => setMode("expanded")}
          className={`px-2.5 py-1 rounded-md transition-colors ${mode === "expanded" ? "bg-white/[0.08] text-white" : "hover:text-white/70"}`}
          data-testid="widget-mode-expanded"
        >
          Öppen
        </button>
      </div>

      {/* Fake browser frame */}
      <div className="bg-[#0a1f24]/60 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
        <div className="px-4 py-3 border-b border-white/5 flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/60" />
          <span className="ml-3 text-[10px] text-white/30 font-mono truncate">
            {_safeHostname(websiteUrl) || "erfirma.se"}
          </span>
        </div>

        {/* Fake site content area */}
        <div className="relative bg-gradient-to-b from-white/[0.02] to-transparent h-[520px] p-5">
          {/* Mock page blocks */}
          <div className="space-y-2 opacity-30">
            <div className="h-2 w-2/3 bg-white/20 rounded" />
            <div className="h-2 w-1/2 bg-white/15 rounded" />
            <div className="grid grid-cols-3 gap-2 mt-4">
              <div className="h-16 bg-white/10 rounded" />
              <div className="h-16 bg-white/10 rounded" />
              <div className="h-16 bg-white/10 rounded" />
            </div>
            <div className="h-2 w-3/4 bg-white/12 rounded mt-4" />
            <div className="h-2 w-2/3 bg-white/10 rounded" />
          </div>

          {/* Widget overlay */}
          {mode === "collapsed" ? (
            <CollapsedWidget
              proactive={form.suggested_proactive_message}
              primaryColor={primaryColor}
              onClick={() => setMode("expanded")}
            />
          ) : (
            <ExpandedWidget
              form={form}
              logoUrl={draftLogoUrl}
              primaryColor={primaryColor}
              headerGradient={headerGradient}
              companyInitial={companyInitial}
              history={history}
              sending={sending}
              input={input}
              setInput={setInput}
              onSend={send}
              onClose={() => setMode("collapsed")}
              onReset={resetChat}
              scrollRef={scrollRef}
              disabled={disabled}
              tasting={tasting}
              tastedQuestions={tastedQuestions}
              onStartTasting={startTasting}
              onPickTone={pickTone}
              onCancelTasting={cancelTasting}
            />
          )}
        </div>
      </div>

      <p className="text-[10px] text-white/30 text-center leading-relaxed">
        Så här ser kunden er kundmottagare på er hemsida — uppdateras direkt när du ändrar fälten.
      </p>
    </div>
  );
}

function CollapsedWidget({ proactive, primaryColor, onClick }) {
  return (
    <div className="absolute bottom-4 right-4 flex flex-col items-end gap-2.5">
      {proactive && (
        <button
          onClick={onClick}
          className="bg-white rounded-2xl rounded-br-sm shadow-2xl shadow-black/50 px-4 py-3 text-sm text-slate-800 max-w-[260px] text-left hover:scale-[1.02] transition-transform animate-in fade-in slide-in-from-bottom-2"
          data-testid="widget-proactive-bubble"
        >
          {proactive}
          <div className="text-[10px] text-slate-400 mt-1">Klicka för att svara →</div>
        </button>
      )}
      <button
        onClick={onClick}
        className="w-14 h-14 rounded-full shadow-2xl shadow-black/50 flex items-center justify-center text-white relative hover:scale-105 transition-transform"
        style={{ background: primaryColor }}
        data-testid="widget-bubble-button"
      >
        <MessageSquare className="w-6 h-6" />
        <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#0F2A30] animate-pulse" />
      </button>
    </div>
  );
}

function ExpandedWidget({
  form, logoUrl, primaryColor, headerGradient, companyInitial,
  history, sending, input, setInput, onSend, onClose, onReset, scrollRef, disabled,
  tasting, tastedQuestions, onStartTasting, onPickTone, onCancelTasting,
}) {
  const remainingQuestions = (form.suggested_test_questions || [])
    .filter((q) => !(tastedQuestions || []).includes(q))
    .slice(0, 4);
  return (
    <div
      className="absolute bottom-4 right-4 w-[360px] max-w-[calc(100%-2rem)] bg-white rounded-2xl shadow-2xl shadow-black/50 overflow-hidden flex flex-col"
      style={{ height: "520px" }}
      data-testid="widget-expanded"
    >
      {/* Header */}
      <div
        className="px-4 py-3 text-white flex items-center justify-between gap-2"
        style={{ background: headerGradient }}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={form.company_name}
              className="w-8 h-8 rounded-full object-cover bg-white p-0.5 border-2 border-white/30 shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
              data-testid="widget-logo"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-white/25 flex items-center justify-center font-bold text-sm shrink-0">
              {companyInitial}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold leading-tight truncate" data-testid="widget-company-name">
              {form.company_name || "Kundservice"}
            </div>
            <div className="text-[10px] opacity-90 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-300" /> Online
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onReset} className="text-white/70 hover:text-white p-1 rounded" title="Nollställ" data-testid="widget-reset">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button onClick={onClose} className="text-white/70 hover:text-white p-1 rounded" data-testid="widget-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* AI-disclosure — direkt under headern, ovanför första meddelandet */}
      {!tasting && <AiDisclosureNotice />}

      {/* Messages — hidden when tone-tasting is active so we never get
          nested scroll areas (the tasting panel owns its own scroll). */}
      {!tasting && (
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50" data-testid="onboarding-preview-messages">
          {history.map((m, i) => (
            <div
              key={i}
              className={`text-[13px] leading-snug max-w-[85%] px-3 py-2 rounded-2xl ${
                m.role === "user"
                  ? "text-white ml-auto rounded-br-sm"
                  : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"
              }`}
              style={m.role === "user" ? { background: primaryColor } : undefined}
            >
              {m.content}
            </div>
          ))}
          {sending && (
            <div className="bg-white text-slate-500 border border-slate-200 rounded-2xl rounded-bl-sm px-3 py-2 w-fit text-sm" data-testid="onboarding-preview-typing">
              <span className="inline-block w-1.5 h-1.5 bg-slate-400 rounded-full animate-pulse mr-1" />
              <span className="inline-block w-1.5 h-1.5 bg-slate-400 rounded-full animate-pulse mr-1" style={{ animationDelay: "0.15s" }} />
              <span className="inline-block w-1.5 h-1.5 bg-slate-400 rounded-full animate-pulse" style={{ animationDelay: "0.3s" }} />
            </div>
          )}
        </div>
      )}

      {/* Tone-tasting takes over the entire chat body when active — single
          scroll surface, no widget input visible until the user picks or
          cancels. Eliminates the dual-scrollbar bug entirely. */}
      {tasting && (
        <ToneTastingPanel
          tasting={tasting}
          primaryColor={primaryColor}
          onPick={onPickTone}
          onCancel={onCancelTasting}
        />
      )}

      {/* Test-question chips — 4 max, drives the tone-tasting flow.
          Questions already tasted disappear ("done that one") so the user
          progresses through them without re-doing the same question. */}
      {!tasting && remainingQuestions.length > 0 && (
        <div className="px-3 py-2.5 border-t border-slate-100 bg-white" data-testid="onboarding-preview-quickstart">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
            <Sparkles className="w-3 h-3" style={{ color: primaryColor }} />
            <span>Tryck och se hur boten kan svara</span>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {remainingQuestions.map((q, i) => (
              <button
                key={q}
                onClick={() => onStartTasting(q)}
                disabled={disabled || sending}
                className="text-[11px] text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-full px-2.5 py-1 max-w-[200px] truncate transition-colors disabled:opacity-50 animate-in fade-in slide-in-from-bottom-1 duration-200"
                title={q}
                data-testid={`onboarding-preview-quick-${i}`}
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Coach copy — shown instead of generic chips when the scrape failed
          or no services have been entered yet. Prevents the user from firing
          off questions that the bot has no real context to answer. */}
      {!tasting
        && remainingQuestions.length === 0
        && (tastedQuestions?.length || 0) === 0
        && !((form.suggested_services || []).some((s) => s && String(s).trim())) && (
        <div
          className="px-3 py-2.5 border-t border-slate-100 bg-amber-50/60"
          data-testid="onboarding-preview-coach-empty"
        >
          <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 mb-1 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            <span>Lite mer info behövs</span>
          </div>
          <div className="text-[11px] text-amber-900/80 leading-snug">
            Fyll i några tjänster först, så kan vi föreslå bättre testfrågor.
          </div>
        </div>
      )}

      {/* Friendly "done" indicator when all suggested questions have been tasted */}
      {!tasting && remainingQuestions.length === 0 && (tastedQuestions?.length || 0) > 0 && (
        <div className="px-3 py-2 border-t border-slate-100 bg-emerald-50/50 flex items-center gap-1.5 text-[11px] text-emerald-700">
          <Check className="w-3 h-3" />
          <span>Snyggt! Tonen är satt — fortsätt gärna med en egen fråga nedan.</span>
        </div>
      )}

      {/* Input — hidden during tone tasting so there is no confusion about
          where to act, and to keep a single visual focus on the 3 cards. */}
      {!tasting && (
        <div className="p-2.5 border-t border-slate-200 bg-white flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") onSend(); }}
            placeholder={disabled ? "Förhandsgranskning låst" : "Skriv en testfråga..."}
            disabled={disabled || sending}
            className="flex-1 text-sm px-3 py-2 rounded-lg border border-slate-200 text-slate-900 placeholder-slate-400 bg-white focus:outline-none focus:ring-2 focus:ring-offset-1 transition-shadow disabled:opacity-50"
            style={{ '--tw-ring-color': primaryColor }}
            data-testid="onboarding-preview-input"
          />
          <button
            onClick={onSend}
            disabled={disabled || sending || !input.trim()}
            className="w-10 h-10 rounded-lg text-white flex items-center justify-center disabled:opacity-40 transition-opacity"
            style={{ background: primaryColor }}
            data-testid="onboarding-preview-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      )}
    </div>
  );
}

// ─ Tone tasting overlay: shows 3 reply cards (one per tone profile) so the
// customer can pick the voice that fits their brand. Renders as a natural
// flex-1 sibling of the message list (NOT absolute) so there is exactly one
// scroll area inside the widget while tasting is active.
const TONE_ACCENTS = {
  "kort-och-rak":            { hue: "#1f6db8", soft: "#e5efff", emoji: "⚡" },
  "varm-och-personlig":      { hue: "#c2410c", soft: "#ffeadd", emoji: "🤝" },
  "professionell-och-serviceinriktad": { hue: "#0f766e", soft: "#d5f4ee", emoji: "🎯" },
  "lokal-och-vardaglig":     { hue: "#92400e", soft: "#fef0d7", emoji: "🏡" },
  "premium-och-trygg":       { hue: "#5b21b6", soft: "#ece5ff", emoji: "✨" },
};
const TONE_DEFAULT = { hue: "#334155", soft: "#f1f5f9", emoji: "💬" };

function ToneTastingPanel({ tasting, primaryColor, onPick, onCancel }) {
  return (
    <div
      className="flex-1 min-h-0 flex flex-col bg-gradient-to-b from-slate-50 to-slate-100"
      data-testid="onboarding-tone-tasting"
    >
      {/* Sticky header */}
      <div className="px-3.5 py-2.5 border-b border-slate-200 flex items-start justify-between gap-2 bg-white">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-0.5">
            Välj tonen
          </div>
          <div className="text-[12px] text-slate-700 leading-snug">
            Vilket av de tre svaren känns mest som ert företag?
          </div>
        </div>
        <button
          onClick={onCancel}
          className="text-slate-400 hover:text-slate-700 p-0.5 -mr-1"
          data-testid="onboarding-tone-tasting-cancel"
          aria-label="Stäng"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* The customer's question — anchored at the top, NOT inside the scroll */}
      <div className="px-3 pt-2.5 pb-2 bg-white border-b border-slate-100">
        <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Din fråga</div>
        <div
          className="text-[12px] text-white px-3 py-2 rounded-2xl rounded-br-sm inline-block max-w-full"
          style={{ background: primaryColor }}
        >
          {tasting.question}
        </div>
      </div>

      {/* SINGLE scroll surface — three response cards */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {tasting.loading && (
          <div className="flex items-center justify-center py-10">
            <div className="flex flex-col items-center gap-2 text-slate-500 text-[12px]">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: primaryColor }} />
              <span>Skriver tre olika svar i parallella toner…</span>
            </div>
          </div>
        )}

        {tasting.error && !tasting.loading && (
          <div className="text-[12px] text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            {tasting.error}
          </div>
        )}

        {!tasting.loading && tasting.variants?.map((v, i) => {
          const accent = TONE_ACCENTS[v.key] || TONE_DEFAULT;
          const isPicked = tasting.pickedTone === v.key;
          const isFaded = tasting.pickedTone && !isPicked;
          return (
            <div
              key={v.key}
              className={`relative bg-white rounded-xl transition-all overflow-hidden ${
                isPicked
                  ? "ring-2 ring-emerald-400 shadow-lg shadow-emerald-500/15"
                  : isFaded
                  ? "border border-slate-200 opacity-40"
                  : "border border-slate-200 hover:border-slate-300 hover:shadow-md"
              }`}
              data-testid={`onboarding-tone-variant-${v.key}`}
            >
              {/* Colored accent strip on the left to differentiate the 3 */}
              <div
                className="absolute top-0 left-0 bottom-0 w-1"
                style={{ background: accent.hue }}
                aria-hidden="true"
              />
              <div className="pl-3.5 pr-3 py-3">
                {/* Tone label header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="shrink-0 w-6 h-6 rounded-md text-[12px] font-bold flex items-center justify-center"
                      style={{ background: accent.soft, color: accent.hue }}
                      aria-hidden="true"
                    >
                      {i + 1}
                    </span>
                    <div className="min-w-0">
                      <div className="text-[12px] font-bold leading-tight" style={{ color: accent.hue }}>
                        {v.label}
                      </div>
                      <div className="text-[10px] text-slate-500 leading-tight truncate">
                        {v.subtitle}
                      </div>
                    </div>
                  </div>
                  {isPicked && (
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600 flex items-center gap-1 shrink-0">
                      <Check className="w-3 h-3" /> Vald
                    </div>
                  )}
                </div>

                {/* The reply, styled like an actual chatbot bubble so the customer
                    immediately recognizes "this is how the bot would talk". */}
                <div className="bg-slate-50 border border-slate-200/70 rounded-2xl rounded-tl-sm px-3 py-2.5 mb-2.5">
                  <div className="text-[13px] text-slate-800 leading-relaxed whitespace-pre-line">
                    {v.reply}
                  </div>
                </div>

                <button
                  onClick={() => onPick(v)}
                  disabled={!!tasting.pickedTone}
                  className="w-full text-[12px] font-semibold text-white rounded-lg py-2 transition-all disabled:opacity-40 flex items-center justify-center gap-1.5"
                  style={{ background: accent.hue }}
                  data-testid={`onboarding-tone-pick-${v.key}`}
                >
                  {isPicked ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Sparar tonen…</span>
                    </>
                  ) : (
                    <>
                      <span>Välj {v.label.toLowerCase()}</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}

        {!tasting.loading && !tasting.error && (
          <div className="text-[10px] text-slate-400 text-center pt-1 leading-relaxed">
            Tonen sparas direkt — du kan alltid byta i editorn.
          </div>
        )}
      </div>
    </div>
  );
}

// Lighten a #rrggbb color by `pct` percentage points toward white.
function _lightenHex(hex, pct) {
  try {
    const h = hex.replace("#", "");
    if (h.length !== 6) return hex;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const lighten = (c) => Math.min(255, Math.round(c + ((255 - c) * pct) / 100));
    const toHex = (c) => c.toString(16).padStart(2, "0");
    return `#${toHex(lighten(r))}${toHex(lighten(g))}${toHex(lighten(b))}`;
  } catch (e) {
    return hex;
  }
}

function _safeHostname(url) {
  try { return new URL(url).hostname; } catch (e) { return null; }
}

// Compact a long URL for display: "https://x.se/very/long/path/file.png" → "x.se/.../file.png"
function _shortenUrl(url, maxLen = 42) {
  if (!url) return "";
  if (url.length <= maxLen) return url;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    const parts = u.pathname.split("/").filter(Boolean);
    const tail = parts[parts.length - 1] || "";
    const compact = parts.length > 1 ? `${host}/…/${tail}` : `${host}${u.pathname}`;
    return compact.length <= maxLen ? compact : compact.slice(0, maxLen - 1) + "…";
  } catch (e) {
    return url.length > maxLen ? url.slice(0, maxLen - 1) + "…" : url;
  }
}
